import {
  CircularProgress,
  Paper,
  Box,
  Typography,
  Stack,
  SxProps
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRowModel,
  GridRowSelectionModel
} from '@mui/x-data-grid';

import { useTranslations } from 'context/TranslationContext';

import { dashboardGridTitle } from 'styles/components/Charts';
import { commonDataGridContainerStyle } from 'styles/components/DataGridStyle';
import { invoicesDataGrid } from 'styles/components/InvoicesDataGridStyle';

interface DashboardGridProps {
  title: string;
  data: GridRowModel[] | undefined;
  columns: GridColDef[];
  getRowId: (row: GridRowModel) => string | number;
  sx?: SxProps;
  selectedRows?: GridRowSelectionModel;
  onRowSelectionChange?: (newSelection: GridRowSelectionModel) => void;
  noDataMessage?: string;
  customStyles?: SxProps;
}

export const DashboardGrid = ({
  title,
  data,
  columns,
  getRowId,
  sx,
  selectedRows,
  onRowSelectionChange,
  noDataMessage,
  customStyles
}: DashboardGridProps) => {
  const { translate } = useTranslations();

  return (
    <Paper elevation={4} sx={{ ...commonDataGridContainerStyle, ...sx }}>
      <Box display="flex" flexDirection="column" height="100%">
        <Box sx={{ paddingY: 2 }}>
          <Typography sx={dashboardGridTitle} align="left">
            {title}
          </Typography>
        </Box>
        <Box flex={1} overflow="auto">
          {data ? (
            <DataGrid
              columns={columns}
              getRowId={(row) => getRowId(row)}
              rows={data}
              rowHeight={30}
              columnHeaderHeight={35}
              sx={{
                ...invoicesDataGrid,
                '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
                  cursor: 'default'
                },
                height: '100%',
                fontSize: '0.6rem',
                '& .MuiDataGrid-columnHeaders': {
                  fontSize: '0.6rem'
                },
                '& .MuiDataGrid-container--top [role=row]': {
                  backgroundColor: 'rgb(249, 249, 249) !important'
                },
                '& .MuiDataGrid-columnHeader': {
                  height: '35px !important'
                },
                '& .MuiDataGrid-cell': {
                  padding: '4px'
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold'
                },
                ...customStyles
              }}
              disableRowSelectionOnClick
              pageSizeOptions={[]}
              hideFooter
              rowSelectionModel={selectedRows}
              onRowSelectionModelChange={onRowSelectionChange}
              localeText={{
                noRowsLabel:
                  noDataMessage || translate('messages.noDataAvailable')
              }}
            />
          ) : (
            <Stack alignItems="center" justifyContent="center" height="100%">
              <CircularProgress />
            </Stack>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
